import {TextField, type TextFieldProps} from '@mui/material'
import type {ReactElement} from 'react'
import {useEffect, useRef, useState} from 'react'

type Props = Omit<TextFieldProps, 'value' | 'onChange' | 'type' | 'error'> & {
  // null means the field is empty.
  value: number | null
  onChange: (value: number | null) => void
  min?: number
  // Shown under the field while it is empty, in place of `helperText`.
  emptyError?: string
}

const parse = (text: string): number | null => {
  if (text.trim() === '') return null
  const number = Number(text)
  return Number.isFinite(number) ? number : null
}

/**
 * A whole-number input that lets people type (AB#6723).
 *
 * Clamping on every keystroke made the field fight the user: clearing it
 * snapped straight back to the minimum, so typing "3" after clearing gave "13".
 * Here the text is the user's while they type — any whole number at or above
 * `min` is reported as they go, an empty field is reported as null — and it is
 * only tidied (rounded down, raised to `min`) when they leave the field. An
 * empty field stays empty and says so; the caller refuses to save it rather
 * than silently using the minimum.
 */
const WholeNumberField = ({
  value,
  onChange,
  min = 1,
  emptyError,
  helperText,
  onBlur,
  inputProps,
  ...rest
}: Props): ReactElement => {
  const [text, setText] = useState(value === null ? '' : String(value))
  const focused = useRef(false)

  // Follow the value when it changes from outside (a template loaded, another
  // field filled this one in) — never while the user is typing in it.
  useEffect(() => {
    if (!focused.current) setText(value === null ? '' : String(value))
  }, [value])

  const empty = text.trim() === ''

  return (
    <TextField
      {...rest}
      type="number"
      value={text}
      error={empty && Boolean(emptyError)}
      helperText={empty && emptyError ? emptyError : helperText}
      inputProps={{min, step: 1, ...inputProps}}
      onFocus={(event) => {
        focused.current = true
        rest.onFocus?.(event)
      }}
      onChange={(event) => {
        const next = event.target.value
        setText(next)
        const number = parse(next)
        if (number === null) {
          onChange(null)
        } else if (Number.isInteger(number) && number >= min) {
          onChange(number)
        }
      }}
      onBlur={(event) => {
        focused.current = false
        const number = parse(text)
        if (number !== null) {
          const tidy = Math.max(min, Math.floor(number))
          setText(String(tidy))
          onChange(tidy)
        }
        onBlur?.(event)
      }}
    />
  )
}

export default WholeNumberField
